import React, { useEffect, useState } from 'react';
import { leaveRequestAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const LeaveRequest = () => {
  const { user } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    applieddate: new Date().toISOString().split('T')[0],
    leave_type: 'festival_leave',
    leave_from_date: '',
    leave_to_date: '',
    reason: '',
    document: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requestsRes, balanceRes] = await Promise.all([
        leaveRequestAPI.getLeaveRequests(),
        leaveRequestAPI.getLeaveBalance()
      ]);

      if (requestsRes.data.success) {
        setLeaveRequests(requestsRes.data.data);
      }

      if (balanceRes.data.success) {
        setLeaveBalance(balanceRes.data.data);
      }
    } catch (error) {
      toast.error('Error fetching data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = () => {
    setFormData({
      applieddate: new Date().toISOString().split('T')[0],
      leave_type: 'festival_leave',
      leave_from_date: '',
      leave_to_date: '',
      reason: '',
      document: null
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.leave_from_date || !formData.leave_to_date) {
      toast.error('Please select leave dates');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('applieddate', formData.applieddate);
      submitData.append('leave_type', formData.leave_type);
      submitData.append('leave_from_date', formData.leave_from_date);
      submitData.append('leave_to_date', formData.leave_to_date);
      submitData.append('reason', formData.reason);
      
      if (formData.document) {
        submitData.append('document', formData.document);
      }

      const response = await leaveRequestAPI.applyLeave(submitData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error applying for leave');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this leave request?')) {
      return;
    }

    try {
      const response = await leaveRequestAPI.deleteLeaveRequest(id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting leave request');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getLeaveTypeLabel = (type) => {
    const labels = {
      festival_leave: 'Festival',
      emergency_leave: 'Emergency',
      regular_leave: 'Regular'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Leave Requests</h1>
          <button
            onClick={handleApplyLeave}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors flex items-center gap-2"
          >
            <FiPlus /> Apply Leave
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Staff</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leave Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leave Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Apply Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin Remark</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leaveRequests.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No leave requests found
                  </td>
                </tr>
              ) : (
                leaveRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{request.applied_by}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{getLeaveTypeLabel(request.leave_type)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(request.leave_from).toLocaleDateString()} - {new Date(request.leave_to).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{request.leave_days}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{new Date(request.applieddate).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span 
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusColor(request.status)}`}
                        title={request.employee_remark}
                      >
                        {request.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{request.admin_remark || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      {request.status === 'pending' && (
                        <button
                          onClick={() => handleDelete(request.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Apply Leave Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Add Details</h2>
            </div>
            <div className="p-8 overflow-y-auto">
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Apply Date
                    </label>
                    <input
                      type="date"
                      value={formData.applieddate}
                      onChange={(e) => setFormData({ ...formData, applieddate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Available Leave <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.leave_type}
                      onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      required
                    >
                      <option value="festival_leave">
                        Festival ({leaveBalance?.festival_leave || 0})
                      </option>
                      <option value="emergency_leave">
                        Emergency ({leaveBalance?.emergency_leave || 0})
                      </option>
                      <option value="regular_leave">
                        Regular ({leaveBalance?.regular_leave || 0})
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Leave From Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.leave_from_date}
                      onChange={(e) => setFormData({ ...formData, leave_from_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Leave To Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.leave_to_date}
                      onChange={(e) => setFormData({ ...formData, leave_to_date: e.target.value })}
                      min={formData.leave_from_date}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason
                    </label>
                    <textarea
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none resize-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attach Document
                    </label>
                    <input
                      type="file"
                      onChange={(e) => setFormData({ ...formData, document: e.target.files[0] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRequest;
